import React, { useState, useEffect } from 'react';
import DynamicBackground from '../../components/DynamicBackground';
import CreditsDisplay from '../../components/CreditsDisplay';
import '../../styles/darkMode.css';
import './record-deal-writer.css';
import { Agreement } from './record-deal-writer-struct';
import { API_BASE_URL } from '../../config';
import { useUser } from '../../contexts/UserContext';
import { storeRecordDeal } from '../../services/contract-generation.service';
import { deductCredit } from '../../services/user.service';

export function downloadFile(data: string | ArrayBuffer, filename: string, mimeType: string) {
  const element = document.createElement("a");
  const file = new Blob([data], { type: mimeType });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(element.href);
}


// Define the steps for the workflow
const STEPS = [
  { id: 1, title: "Basic Information", description: "Tell us about the parties involved" },
  { id: 2, title: "Deal Terms", description: "Define the core agreement details" },
  { id: 3, title: "Track List", description: "Add the music tracks covered" },
  { id: 4, title: "Royalty Splits", description: "Set up how royalties are divided" },
  { id: 5, title: "Metadata & Accounting", description: "Configure reporting and payments" },
  { id: 6, title: "Termination & Legal", description: "Finalize legal terms and conditions" }
];

const RecordDealWriter: React.FC = () => {
  const { refreshUser, user } = useUser();
  const [currentStep, setCurrentStep] = useState(1);
  const [agreement, setAgreement] = useState<Agreement>({
    agreementDate: '',
    label: {
      name: '',
      jurisdiction: '',
      address: ''
    },
    artist: {
      name: '',
      address: ''
    },
    relationshipType: '',
    licenseOrAssignment: '',
    territory: '',
    termYears: 0,
    sellOffMonths: 0,
    trackList: [],
    optionalFutureWorks: false,
    recoupableCosts: [],
    royaltySplits: {
      salesStreams: '',
      sync: '',
      remixes: ''
    },
    metadataDuties: {
      submitter: '',
      platforms: ['soundcloud', 'spotify']
    },
    accounting: {
      reportFrequency: '',
      paymentAfter: '',
      auditNoticeDays: 0,
      holdbackPercent: 0,
      holdbackRelease: ''
    },
    termination: {
      breachNoticeDays: 0,
      reversionOnNoReleaseMonths: 0,
      curePeriodDays: 0
    },
    disputeJurisdiction: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log('State changed - isLoading:', isLoading, 'showReview:', showReview);
    console.log('Rendering - showReview:', showReview, 'isLoading:', isLoading);
  }, [isLoading, showReview]);

  // Validation functions
  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    
    switch (step) {
      case 1: // Basic Information
        if (!agreement.agreementDate) errors.agreementDate = 'Please select an agreement date';
        if (!agreement.label.name.trim()) errors['label.name'] = 'Please enter the label name';
        if (!agreement.label.jurisdiction.trim()) errors['label.jurisdiction'] = 'Please enter the label jurisdiction';
        if (!agreement.label.address.trim()) errors['label.address'] = 'Please enter the label address';
        if (!agreement.artist.name.trim()) errors['artist.name'] = 'Please enter the artist name';
        if (!agreement.artist.address.trim()) errors['artist.address'] = 'Please enter the artist address';
        break;
      case 2: // Deal Terms
        if (!agreement.relationshipType.trim()) errors.relationshipType = 'Please describe the relationship type';
        if (!agreement.licenseOrAssignment) errors.licenseOrAssignment = 'Please select license or assignment';
        if (!agreement.territory.trim()) errors.territory = 'Please specify the territory';
        if (agreement.termYears <= 0) errors.termYears = 'Please enter a valid term in years';
        if (agreement.sellOffMonths < 0) errors.sellOffMonths = 'Please enter a valid sell-off period';
        break;
      case 3: // Track List
        if (agreement.trackList.length === 0) errors.trackList = 'Please add at least one track';
        agreement.trackList.forEach((track, index) => {
          if (!track.title.trim()) errors[`track.${index}.title`] = 'Track title is required';
          if (!track.isrc.trim()) errors[`track.${index}.isrc`] = 'ISRC is required';
        });
        break;
      case 4: // Royalty Splits
        if (!agreement.royaltySplits.salesStreams.trim()) errors['royaltySplits.salesStreams'] = 'Please specify sales & streaming royalty split';
        if (!agreement.royaltySplits.sync.trim()) errors['royaltySplits.sync'] = 'Please specify sync royalty split';
        if (!agreement.royaltySplits.remixes.trim()) errors['royaltySplits.remixes'] = 'Please specify remix royalty split';
        break;
      case 5: // Metadata & Accounting
        if (!agreement.metadataDuties.submitter.trim()) errors['metadataDuties.submitter'] = 'Please specify who submits metadata';
        if (agreement.accounting.auditNoticeDays <= 0) errors['accounting.auditNoticeDays'] = 'Please enter valid audit notice days';
        if (agreement.accounting.holdbackPercent < 0 || agreement.accounting.holdbackPercent > 100) {
          errors['accounting.holdbackPercent'] = 'Holdback percent must be between 0 and 100';
        }
        break;
      case 6: // Termination & Legal
        if (agreement.termination.breachNoticeDays <= 0) errors['termination.breachNoticeDays'] = 'Please enter valid breach notice days';
        if (agreement.termination.reversionOnNoReleaseMonths <= 0) errors['termination.reversionOnNoReleaseMonths'] = 'Please enter valid reversion period';
        if (agreement.termination.curePeriodDays <= 0) errors['termination.curePeriodDays'] = 'Please enter valid cure period days';
        if (!agreement.disputeJurisdiction.trim()) errors.disputeJurisdiction = 'Please specify dispute jurisdiction';
        break;
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const goToStep = (step: number) => {
    if (step <= currentStep || validateStep(currentStep)) {
      setCurrentStep(step);
    }
  };

  // Field explanation helper
  const getFieldExplanation = (fieldName: string): string => {
    const explanations: Record<string, string> = {
      'agreementDate': 'The date when this agreement becomes effective. This is typically the date both parties sign.',
      'label.name': 'The legal name of the record label or company entering into this agreement.',
      'label.jurisdiction': 'The country or state where the label is legally incorporated or based.',
      'label.address': 'The complete business address of the record label.',
      'artist.name': 'The legal name of the artist or band entering into this agreement.',
      'artist.address': 'The complete address of the artist or band.',
      'relationshipType': 'Describe the nature of the relationship (e.g., "Exclusive Recording Agreement", "Distribution Deal").',
      'licenseOrAssignment': 'License means the label gets rights to use the music for a limited time. Assignment means the label gets permanent ownership.',
      'territory': 'The geographical areas where this agreement applies (e.g., "Worldwide", "North America", "United States").',
      'termYears': 'How long the agreement lasts in years. This is the main contract period.',
      'sellOffMonths': 'After the contract ends, how many months the label can continue selling existing inventory.',
      'title': 'The name of the music track or song.',
      'isrc': 'International Standard Recording Code - a unique identifier for each recording.',
      'salesStreams': 'How royalties are split for sales and streaming (e.g., "50/50", "Artist gets 15% of net receipts").',
      'sync': 'How royalties are split for synchronization (music in TV, film, ads) (e.g., "50/50", "Artist gets 50%").',
      'remixes': 'How royalties are split for remixes and covers (e.g., "50/50", "Artist gets 25%").',
      'submitter': 'Who is responsible for submitting metadata to streaming platforms and distributors.',
      'platforms': 'Which music platforms the metadata will be submitted to (comma-separated list).',
      'reportFrequency': 'How often the label provides royalty reports (e.g., "Quarterly", "Semi-annually", "Annually").',
      'paymentAfter': 'When payments are made after the reporting period (e.g., "30 days", "60 days").',
      'auditNoticeDays': 'How many days advance notice the artist must give before auditing the label\'s books.',
      'holdbackPercent': 'Percentage of royalties held back as security (0-100%).',
      'holdbackRelease': 'When the holdback is released (e.g., "After 2 years", "Upon album release").',
      'breachNoticeDays': 'How many days notice must be given before terminating for breach of contract.',
      'reversionOnNoReleaseMonths': 'If the label doesn\'t release the music within this many months, rights revert to the artist.',
      'curePeriodDays': 'How many days the breaching party has to fix the problem before termination.',
      'disputeJurisdiction': 'Which court system will handle any legal disputes (e.g., "New York State Courts", "Los Angeles County").',
      'recoupableCosts': 'What expenses the label can recover from royalties before paying the artist (comma-separated).'
    };
    return explanations[fieldName] || '';
  };

  const handleFinalSubmit = async () => {
    // Validate all steps before submitting
    let allStepsValid = true;
    for (let step = 1; step <= STEPS.length; step++) {
      if (!validateStep(step)) {
        allStepsValid = false;
        break;
      }
    }
    
    if (!allStepsValid) {
      alert('Please complete all required fields before submitting.');
      return;
    }
    
    if (!disclaimerAccepted) {
      alert('Please accept the disclaimer before submitting.');
      return;
    }
    
    console.log('Submit button clicked, setting loading to true');
    setIsLoading(true);
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (!storedUser.idToken) {
        throw new Error('User not authenticated');
      }

      // Deduct credit before processing
      console.log('Deducting credit...');
      try {
        await deductCredit(storedUser.id);
        console.log('Credit deducted successfully');
      } catch (creditError) {
        console.error('Credit deduction failed:', creditError);
        throw new Error(creditError instanceof Error ? creditError.message : 'Failed to deduct credit');
      }

      console.log('Making API call...');
      const response = await fetch(`${API_BASE_URL}/api/write/record-deal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedUser.idToken}`
        },
        body: JSON.stringify(agreement),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit agreement');
      }

      console.log('API call successful, processing response...');
      
      // Check content type
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      let markdownText: string;
      if (contentType && contentType.includes('application/pdf')) {
        // If it's already a PDF, get it as ArrayBuffer
        const result = await response.arrayBuffer();
        console.log('Received PDF ArrayBuffer of size:', result.byteLength);
        
        // Convert to text for review (this will show binary data as text)
        const uint8Array = new Uint8Array(result);
        markdownText = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('');
      } else {
        // Get as text (Markdown format)
        markdownText = await response.text();
        console.log('Received Markdown response:', markdownText.substring(0, 200) + '...');
      }
      
      setReviewText(markdownText);
      console.log('Setting showReview to true');
      setShowReview(true);
      
      // Refresh user credits after successful operation
      await refreshUser();
      
    } catch (error) {
      console.error('Error submitting agreement:', error);
      alert(error instanceof Error ? error.message : 'Error submitting agreement. Please try again.');
      // Refresh user to ensure credits are in sync
      await refreshUser();
    } finally {
      console.log('Setting loading to false');
      setIsLoading(false);
    }
  };

  const handleConfirmAndGenerate = async () => {
    try {
      // Store the contract generation
      await storeRecordDeal(agreement, reviewText);
      console.log('Contract generation stored successfully');
      
      // Refresh user credits
      await refreshUser();
      
      // Route to contract generations page with highlight parameter
      window.location.href = '/contract-generations?highlight=recent';
    } catch (error) {
      console.error('Error storing contract generation:', error);
      alert('Error storing contract generation. Please try again.');
    }
  };

  const handleCancelReview = () => {
    setShowReview(false);
    setReviewText('');
  };

  const handleDownloadMarkdown = () => {
    downloadFile(reviewText, 'agreement.md', 'text/markdown');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setAgreement(prev => {
        const parentObj = prev[parent as keyof Agreement] as Record<string, any>;
        return {
          ...prev,
          [parent]: {
            ...parentObj,
            [child]: type === 'number' ? Number(value) : value
          }
        };
      });
    } else {
      setAgreement(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value
      }));
    }
  };

  const handleArrayInputChange = (field: string, value: string) => {
    console.log('handleArrayInputChange called with:', field, value);
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setAgreement(prev => {
        const parentObj = prev[parent as keyof Agreement] as Record<string, any>;
        return {
          ...prev,
          [parent]: {
            ...parentObj,
            [child]: value.split(',').map(item => item.trim())
          }
        };
      });
    } else {
      setAgreement(prev => ({
        ...prev,
        [field]: value.split(',').map(item => item.trim())
      }));
    }
  };

  const handleTrackListChange = (index: number, field: string, value: string) => {
    setAgreement(prev => ({
      ...prev,
      trackList: prev.trackList.map((track, i) => 
        i === index ? { ...track, [field]: value } : track
      )
    }));
  };

  const addTrack = () => {
    setAgreement(prev => ({
      ...prev,
      trackList: [...prev.trackList, { title: '', isrc: '' }]
    }));
  };

  // Step components
  const renderStep1 = () => (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Basic Information</h2>
        <p className="step-description">Let's start with the essential details about the parties involved in this agreement.</p>
      </div>
      
      <div className="form-section">
        <div className="form-group">
          <label className="field-label">Agreement Date *</label>
          <input
            type="date"
            name="agreementDate"
            value={agreement.agreementDate}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors.agreementDate ? 'error' : ''}`}
            required
          />
          <div className="field-explanation">{getFieldExplanation('agreementDate')}</div>
          {fieldErrors.agreementDate && <div className="field-error">{fieldErrors.agreementDate}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">Label Name *</label>
          <input
            type="text"
            name="label.name"
            value={agreement.label.name}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['label.name'] ? 'error' : ''}`}
            placeholder="Enter the record label's legal name"
            required
          />
          <div className="field-explanation">{getFieldExplanation('label.name')}</div>
          {fieldErrors['label.name'] && <div className="field-error">{fieldErrors['label.name']}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">Label Jurisdiction *</label>
          <input
            type="text"
            name="label.jurisdiction"
            value={agreement.label.jurisdiction}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['label.jurisdiction'] ? 'error' : ''}`}
            placeholder="e.g., California, New York, United Kingdom"
            required
          />
          <div className="field-explanation">{getFieldExplanation('label.jurisdiction')}</div>
          {fieldErrors['label.jurisdiction'] && <div className="field-error">{fieldErrors['label.jurisdiction']}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">Label Address *</label>
          <input
            type="text"
            name="label.address"
            value={agreement.label.address}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['label.address'] ? 'error' : ''}`}
            placeholder="Complete business address"
            required
          />
          <div className="field-explanation">{getFieldExplanation('label.address')}</div>
          {fieldErrors['label.address'] && <div className="field-error">{fieldErrors['label.address']}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">Artist Name *</label>
          <input
            type="text"
            name="artist.name"
            value={agreement.artist.name}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['artist.name'] ? 'error' : ''}`}
            placeholder="Enter the artist's or band's legal name"
            required
          />
          <div className="field-explanation">{getFieldExplanation('artist.name')}</div>
          {fieldErrors['artist.name'] && <div className="field-error">{fieldErrors['artist.name']}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">Artist Address *</label>
          <input
            type="text"
            name="artist.address"
            value={agreement.artist.address}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['artist.address'] ? 'error' : ''}`}
            placeholder="Complete address"
            required
          />
          <div className="field-explanation">{getFieldExplanation('artist.address')}</div>
          {fieldErrors['artist.address'] && <div className="field-error">{fieldErrors['artist.address']}</div>}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Deal Terms</h2>
        <p className="step-description">Define the core terms of your recording agreement.</p>
      </div>
      
      <div className="form-section">
        <div className="form-group">
          <label className="field-label">Relationship Type *</label>
          <input
            type="text"
            name="relationshipType"
            value={agreement.relationshipType}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors.relationshipType ? 'error' : ''}`}
            placeholder="e.g., Exclusive Recording Agreement, Distribution Deal"
            required
          />
          <div className="field-explanation">{getFieldExplanation('relationshipType')}</div>
          {fieldErrors.relationshipType && <div className="field-error">{fieldErrors.relationshipType}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">License or Assignment *</label>
          <select
            name="licenseOrAssignment"
            value={agreement.licenseOrAssignment}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors.licenseOrAssignment ? 'error' : ''}`}
            required
          >
            <option value="">Select an option...</option>
            <option value="license">License (Limited Rights)</option>
            <option value="assignment">Assignment (Full Rights)</option>
          </select>
          <div className="field-explanation">{getFieldExplanation('licenseOrAssignment')}</div>
          {fieldErrors.licenseOrAssignment && <div className="field-error">{fieldErrors.licenseOrAssignment}</div>}
        </div>

        <div className="form-group">
          <label className="field-label">Territory *</label>
          <input
            type="text"
            name="territory"
            value={agreement.territory}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors.territory ? 'error' : ''}`}
            placeholder="e.g., Worldwide, North America, United States"
            required
          />
          <div className="field-explanation">{getFieldExplanation('territory')}</div>
          {fieldErrors.territory && <div className="field-error">{fieldErrors.territory}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Term (Years) *</label>
            <input
              type="number"
              name="termYears"
              value={agreement.termYears}
              onChange={handleInputChange}
              className={`field-input ${fieldErrors.termYears ? 'error' : ''}`}
              min="0"
              step="0.5"
              placeholder="e.g., 3"
              required
            />
            <div className="field-explanation">{getFieldExplanation('termYears')}</div>
            {fieldErrors.termYears && <div className="field-error">{fieldErrors.termYears}</div>}
          </div>

          <div className="form-group">
            <label className="field-label">Sell-off Period (Months) *</label>
            <input
              type="number"
              name="sellOffMonths"
              value={agreement.sellOffMonths}
              onChange={handleInputChange}
              className={`field-input ${fieldErrors.sellOffMonths ? 'error' : ''}`}
              min="0"
              placeholder="e.g., 12"
              required
            />
            <div className="field-explanation">{getFieldExplanation('sellOffMonths')}</div>
            {fieldErrors.sellOffMonths && <div className="field-error">{fieldErrors.sellOffMonths}</div>}
          </div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="optionalFutureWorks"
              checked={agreement.optionalFutureWorks}
              onChange={(e) => setAgreement(prev => ({ ...prev, optionalFutureWorks: e.target.checked }))}
              className="field-checkbox"
            />
            <span className="checkbox-text">Include optional future works clause</span>
          </label>
          <div className="field-explanation">This allows the label to have first option on future recordings by the artist.</div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Track List</h2>
        <p className="step-description">Add the music tracks that will be covered by this agreement.</p>
      </div>
      
      <div className="form-section">
        {agreement.trackList.map((track, index) => (
          <div key={index} className="track-entry">
            <div className="form-group">
              <label className="field-label">Track Title *</label>
              <input
                type="text"
                value={track.title}
                onChange={(e) => handleTrackListChange(index, 'title', e.target.value)}
                className={`field-input ${fieldErrors[`track.${index}.title`] ? 'error' : ''}`}
                placeholder="Enter track title"
                required
              />
              <div className="field-explanation">{getFieldExplanation('title')}</div>
              {fieldErrors[`track.${index}.title`] && <div className="field-error">{fieldErrors[`track.${index}.title`]}</div>}
            </div>
            <div className="form-group">
              <label className="field-label">ISRC *</label>
              <input
                type="text"
                value={track.isrc}
                onChange={(e) => handleTrackListChange(index, 'isrc', e.target.value)}
                className={`field-input ${fieldErrors[`track.${index}.isrc`] ? 'error' : ''}`}
                placeholder="e.g., USRC17607839"
                required
              />
              <div className="field-explanation">{getFieldExplanation('isrc')}</div>
              {fieldErrors[`track.${index}.isrc`] && <div className="field-error">{fieldErrors[`track.${index}.isrc`]}</div>}
            </div>
          </div>
        ))}
        {fieldErrors.trackList && <div className="field-error">{fieldErrors.trackList}</div>}
        <button type="button" onClick={addTrack} className="add-track-button">+ Add Track</button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Royalty Splits</h2>
        <p className="step-description">Define how royalties will be divided between the label and artist.</p>
      </div>
      
      <div className="form-section">
        <div className="form-group">
          <label className="field-label">Sales & Streaming *</label>
          <input
            type="text"
            name="royaltySplits.salesStreams"
            value={agreement.royaltySplits.salesStreams}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['royaltySplits.salesStreams'] ? 'error' : ''}`}
            placeholder="e.g., 50/50, Artist gets 15% of net receipts"
            required
          />
          <div className="field-explanation">{getFieldExplanation('salesStreams')}</div>
          {fieldErrors['royaltySplits.salesStreams'] && <div className="field-error">{fieldErrors['royaltySplits.salesStreams']}</div>}
        </div>
        
        <div className="form-group">
          <label className="field-label">Sync (TV, Film, Ads) *</label>
          <input
            type="text"
            name="royaltySplits.sync"
            value={agreement.royaltySplits.sync}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['royaltySplits.sync'] ? 'error' : ''}`}
            placeholder="e.g., 50/50, Artist gets 50%"
            required
          />
          <div className="field-explanation">{getFieldExplanation('sync')}</div>
          {fieldErrors['royaltySplits.sync'] && <div className="field-error">{fieldErrors['royaltySplits.sync']}</div>}
        </div>
        
        <div className="form-group">
          <label className="field-label">Remixes & Covers *</label>
          <input
            type="text"
            name="royaltySplits.remixes"
            value={agreement.royaltySplits.remixes}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['royaltySplits.remixes'] ? 'error' : ''}`}
            placeholder="e.g., 50/50, Artist gets 25%"
            required
          />
          <div className="field-explanation">{getFieldExplanation('remixes')}</div>
          {fieldErrors['royaltySplits.remixes'] && <div className="field-error">{fieldErrors['royaltySplits.remixes']}</div>}
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Metadata & Accounting</h2>
        <p className="step-description">Set up reporting requirements and payment schedules.</p>
      </div>
      
      <div className="form-section">
        <div className="form-group">
          <label className="field-label">Metadata Submitter *</label>
          <input
            type="text"
            name="metadataDuties.submitter"
            value={agreement.metadataDuties.submitter}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['metadataDuties.submitter'] ? 'error' : ''}`}
            placeholder="e.g., Label, Artist, Third-party service"
            required
          />
          <div className="field-explanation">{getFieldExplanation('submitter')}</div>
          {fieldErrors['metadataDuties.submitter'] && <div className="field-error">{fieldErrors['metadataDuties.submitter']}</div>}
        </div>
        
        <div className="form-group">
          <label className="field-label">Platforms *</label>
          <input
            type="text"
            value={agreement.metadataDuties.platforms.join(', ')}
            onChange={(e) => handleArrayInputChange('metadataDuties.platforms', e.target.value)}
            className="field-input"
            placeholder="e.g., Spotify, Apple Music, SoundCloud"
            required
          />
          <div className="field-explanation">{getFieldExplanation('platforms')}</div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Report Frequency *</label>
            <input
              type="text"
              name="accounting.reportFrequency"
              value={agreement.accounting.reportFrequency}
              onChange={handleInputChange}
              className="field-input"
              placeholder="e.g., Quarterly, Semi-annually"
              required
            />
            <div className="field-explanation">{getFieldExplanation('reportFrequency')}</div>
          </div>
          
          <div className="form-group">
            <label className="field-label">Payment After *</label>
            <input
              type="text"
              name="accounting.paymentAfter"
              value={agreement.accounting.paymentAfter}
              onChange={handleInputChange}
              className="field-input"
              placeholder="e.g., 30 days, 60 days"
              required
            />
            <div className="field-explanation">{getFieldExplanation('paymentAfter')}</div>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Audit Notice (Days) *</label>
            <input
              type="number"
              name="accounting.auditNoticeDays"
              value={agreement.accounting.auditNoticeDays}
              onChange={handleInputChange}
              className={`field-input ${fieldErrors['accounting.auditNoticeDays'] ? 'error' : ''}`}
              min="0"
              placeholder="e.g., 30"
              required
            />
            <div className="field-explanation">{getFieldExplanation('auditNoticeDays')}</div>
            {fieldErrors['accounting.auditNoticeDays'] && <div className="field-error">{fieldErrors['accounting.auditNoticeDays']}</div>}
          </div>
          
          <div className="form-group">
            <label className="field-label">Holdback Percent *</label>
            <input
              type="number"
              name="accounting.holdbackPercent"
              value={agreement.accounting.holdbackPercent}
              onChange={handleInputChange}
              className={`field-input ${fieldErrors['accounting.holdbackPercent'] ? 'error' : ''}`}
              min="0"
              max="100"
              placeholder="e.g., 10"
              required
            />
            <div className="field-explanation">{getFieldExplanation('holdbackPercent')}</div>
            {fieldErrors['accounting.holdbackPercent'] && <div className="field-error">{fieldErrors['accounting.holdbackPercent']}</div>}
          </div>
        </div>
        
        <div className="form-group">
          <label className="field-label">Holdback Release *</label>
          <input
            type="text"
            name="accounting.holdbackRelease"
            value={agreement.accounting.holdbackRelease}
            onChange={handleInputChange}
            className="field-input"
            placeholder="e.g., After 2 years, Upon album release"
            required
          />
          <div className="field-explanation">{getFieldExplanation('holdbackRelease')}</div>
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">Termination & Legal</h2>
        <p className="step-description">Finalize the legal terms and termination conditions.</p>
      </div>
      
      <div className="form-section">
        <div className="form-row">
          <div className="form-group">
            <label className="field-label">Breach Notice (Days) *</label>
            <input
              type="number"
              name="termination.breachNoticeDays"
              value={agreement.termination.breachNoticeDays}
              onChange={handleInputChange}
              className={`field-input ${fieldErrors['termination.breachNoticeDays'] ? 'error' : ''}`}
              min="0"
              placeholder="e.g., 30"
              required
            />
            <div className="field-explanation">{getFieldExplanation('breachNoticeDays')}</div>
            {fieldErrors['termination.breachNoticeDays'] && <div className="field-error">{fieldErrors['termination.breachNoticeDays']}</div>}
          </div>
          
          <div className="form-group">
            <label className="field-label">Reversion Period (Months) *</label>
            <input
              type="number"
              name="termination.reversionOnNoReleaseMonths"
              value={agreement.termination.reversionOnNoReleaseMonths}
              onChange={handleInputChange}
              className={`field-input ${fieldErrors['termination.reversionOnNoReleaseMonths'] ? 'error' : ''}`}
              min="0"
              placeholder="e.g., 24"
              required
            />
            <div className="field-explanation">{getFieldExplanation('reversionOnNoReleaseMonths')}</div>
            {fieldErrors['termination.reversionOnNoReleaseMonths'] && <div className="field-error">{fieldErrors['termination.reversionOnNoReleaseMonths']}</div>}
          </div>
        </div>
        
        <div className="form-group">
          <label className="field-label">Cure Period (Days) *</label>
          <input
            type="number"
            name="termination.curePeriodDays"
            value={agreement.termination.curePeriodDays}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors['termination.curePeriodDays'] ? 'error' : ''}`}
            min="0"
            placeholder="e.g., 30"
            required
          />
          <div className="field-explanation">{getFieldExplanation('curePeriodDays')}</div>
          {fieldErrors['termination.curePeriodDays'] && <div className="field-error">{fieldErrors['termination.curePeriodDays']}</div>}
        </div>
        
        <div className="form-group">
          <label className="field-label">Recoupable Costs *</label>
          <input
            type="text"
            value={agreement.recoupableCosts.join(', ')}
            onChange={(e) => handleArrayInputChange('recoupableCosts', e.target.value)}
            className="field-input"
            placeholder="e.g., Recording costs, Marketing, Promotion"
            required
          />
          <div className="field-explanation">{getFieldExplanation('recoupableCosts')}</div>
        </div>
        
        <div className="form-group">
          <label className="field-label">Dispute Jurisdiction *</label>
          <input
            type="text"
            name="disputeJurisdiction"
            value={agreement.disputeJurisdiction}
            onChange={handleInputChange}
            className={`field-input ${fieldErrors.disputeJurisdiction ? 'error' : ''}`}
            placeholder="e.g., New York State Courts, Los Angeles County"
            required
          />
          <div className="field-explanation">{getFieldExplanation('disputeJurisdiction')}</div>
          {fieldErrors.disputeJurisdiction && <div className="field-error">{fieldErrors.disputeJurisdiction}</div>}
        </div>
        
        <div className="form-group disclaimer-section">
          <label className="disclaimer-checkbox">
            <input
              type="checkbox"
              checked={disclaimerAccepted}
              onChange={(e) => setDisclaimerAccepted(e.target.checked)}
              className="field-checkbox"
            />
            <span className="disclaimer-text">
              I understand that this feature is experimental and the generated agreement should NOT be used as a legally binding document without proper legal review and consultation with a qualified attorney.
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <DynamicBackground>
      <div className="dark-container">
        <CreditsDisplay variant="header" />
        <h1 className="dark-title">Record Deal Agreement Writer</h1>
        
        {!showReview ? (
          <div className="workflow-container">
            {/* Progress Indicator */}
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
                ></div>
              </div>
              <div className="progress-steps">
                {STEPS.map((step) => (
                  <div 
                    key={step.id} 
                    className={`progress-step ${currentStep >= step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
                    onClick={() => goToStep(step.id)}
                  >
                    <div className="step-number">{step.id}</div>
                    <div className="step-info">
                      <div className="step-name">{step.title}</div>
                      <div className="step-desc">{step.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div className="step-container">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}
              {currentStep === 6 && renderStep6()}
            </div>

            {/* Navigation */}
            <div className="step-navigation">
              <button 
                type="button" 
                onClick={prevStep} 
                className="nav-button secondary"
                disabled={currentStep === 1}
              >
                Previous
              </button>
              
              {currentStep < STEPS.length ? (
                <button 
                  type="button" 
                  onClick={nextStep} 
                  className="nav-button primary"
                >
                  Next Step
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                  <button 
                    type="button" 
                    onClick={handleFinalSubmit} 
                    className="nav-button primary"
                    disabled={!disclaimerAccepted || isLoading || (user?.credits === 0 && user?.subscriptionStatus !== 'active')}
                  >
                    {isLoading ? 'Generating Agreement...' : 'Review Agreement'}
                  </button>
                  <p className="dark-text" style={{ fontSize: '0.875rem', margin: 0, opacity: 0.8 }}>
                    💎 This service costs 1 credit
                    {user?.subscriptionStatus === 'active' && ' (Unlimited for subscribers)'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="review-section">
            <h2 className="dark-text">Review Generated Agreement</h2>
            <p className="dark-text">Please review the generated agreement below. You can make any necessary changes before generating the contract.</p>
            
            <div className="form-group">
              <label className="dark-text">Agreement Content (Markdown Format)</label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="dark-textarea"
                rows={20}
                style={{ width: '100%', minHeight: '400px' }}
                placeholder="Review and edit the generated agreement content..."
              />
            </div>
            
            <div className="button-group">
              <button 
                type="button" 
                onClick={handleConfirmAndGenerate}
                className="dark-button primary"
              >
                Generate Contract
              </button>
              <button 
                type="button" 
                onClick={handleDownloadMarkdown}
                className="dark-button"
              >
                Download Markdown
              </button>
              <button 
                type="button" 
                onClick={handleCancelReview}
                className="dark-button secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </DynamicBackground>
  );
};

export default RecordDealWriter; 